"use client";

import { useState, useEffect } from "react";
import { motion, useReducedMotion } from "@/lib/motion";
import { inViewScrollRootMargin } from "@/lib/motion-viewport";
import SectionHeader from "@/components/ui/section-header";
import dynamic from "next/dynamic";

const GlobeComponent = dynamic(() => import("@/components/sections/shared/globe"), {
  ssr: false,
  loading: () => (
    <div className="bg-foreground/[0.03] aspect-square w-full animate-pulse rounded-full" />
  ),
});
const SpeedCanvas = dynamic(() => import("./speed-canvas"), {
  ssr: false,
  loading: () => (
    <div className="border-border bg-foreground/[0.03] aspect-[4/3] w-full animate-pulse rounded-2xl border" />
  ),
});

export default function MobileProductHighlights() {
  const prefersReducedMotion = useReducedMotion();
  const [speed, setSpeed] = useState(45);

  useEffect(() => {
    if (prefersReducedMotion) return;
    const id = setInterval(() => {
      setSpeed(Math.floor(Math.random() * 90) + 10);
    }, 2800);
    return () => clearInterval(id);
  }, [prefersReducedMotion]);

  const fadeUp = (delay = 0) =>
    prefersReducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 22 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, margin: inViewScrollRootMargin },
          transition: { duration: 0.5, delay },
        };

  return (
    <>
      <section
        id="worldwide-coverage-section"
        /* Same band as the homepage's tinted sections — see the note on the
           speed section below for why full-bleed is required. Compact padding
           (py-section-sm) because this band and the one below it are adjacent:
           at full py-section the pair reads as one very tall grey block. */
        className="full-bleed band-wash py-section-sm border-border bg-band relative isolate overflow-hidden border-b"
        aria-labelledby="worldwide-coverage-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp(0)} className="relative order-2 min-w-0 lg:order-1">
              <GlobeComponent ariaLabel="Interactive 3D globe showing global mobile proxy coverage" />
            </motion.div>

            <motion.div {...fadeUp(0.12)} className="order-1 lg:order-2">
              <SectionHeader
                eyebrow="Coverage"
                id="worldwide-coverage-heading"
                heading="Mobile Proxies Worldwide"
                headingClassName="mb-5"
              />
              <p className="text-muted-foreground mb-4 text-lg leading-relaxed">
                Proxidize operates a managed global mobile proxy network across many locations,
                giving customers direct access to reliable, premium pooled mobile IPs without
                needing to build, host, or manage hardware.
              </p>
              <p className="text-muted-foreground mb-4 text-lg leading-relaxed">
                Availability varies by location and network. Choose from the countries, cities, and
                ASN/ISPs currently available in the dashboard.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <section
        id="speed-section"
        /* bg-band is the homepage's alternating section tint (#0e0e10 dark,
           #f6f9fc light) — a step above the page and below the cards, so the
           tiles inside still read as elevated on it.

           full-bleed: the shell constrains sections to max-w-7xl, so without it
           the band would stop at the two frame hairlines instead of running the
           full viewport width. The inner max-w-7xl wrapper keeps the content
           aligned with every other section. */
        className="full-bleed band-wash py-section-sm border-border bg-band relative isolate overflow-hidden border-b"
        aria-labelledby="speed-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp(0)} className="order-2 lg:order-1">
              <SpeedCanvas speed={speed} />
            </motion.div>

            <motion.div {...fadeUp(0.12)} className="order-1 lg:order-2">
              <SectionHeader
                eyebrow="Performance"
                id="speed-heading"
                heading="Reassuringly Fast"
                headingClassName="mb-5"
              />
              <p className="text-muted-foreground mb-6 text-lg leading-relaxed">
                Typical speeds range from{" "}
                <span className="text-foreground font-semibold">10–50 Mbps</span>, with burst speeds
                reaching up to <span className="text-foreground font-semibold">100 Mbps.</span>
              </p>
              <p className="text-muted-foreground mb-8 leading-relaxed">
                For specific speed requirements, a custom SLA is available on request.
              </p>

              <div className="mb-8 grid grid-cols-2 gap-3">
                {[
                  { tier: "Standard", speed: "10–50 Mbps", desc: "Consistent baseline" },
                  { tier: "Burst", speed: "50–100 Mbps", desc: "High-throughput tasks" },
                ].map((item) => (
                  <div
                    key={item.tier}
                    className="border-border/60 bg-foreground/[0.03] rounded-xl border p-3"
                  >
                    <p className="text-muted-foreground mb-0.5 text-[10px] font-bold tracking-wider uppercase">
                      {item.tier}
                    </p>
                    <p className="text-foreground/90 text-sm font-bold">{item.speed}</p>
                    <p className="text-muted-foreground mt-0.5 text-[11px]">{item.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* <section
        id="http3-section"
        className="py-section border-border overflow-hidden border-b"
        aria-labelledby="http3-heading"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <motion.div {...fadeUp(0)}>
              <SectionHeader
                eyebrow="Protocol Support"
                id="http3-heading"
                heading={
                  <>
                    HTTP/3 &amp; QUIC
                    <br />
                    Support
                  </>
                }
                headingClassName="mb-5"
              />
              <p className="text-muted-foreground mb-4 text-lg leading-relaxed">
                Proxidize supports <span className="text-foreground font-semibold">HTTP/3</span> and{" "}
                <span className="text-foreground font-semibold">QUIC</span> for improved connection
                stability and lower latency.
              </p>
              <ul className="mb-8 space-y-2.5">
                {[
                  "Improved connection stability",
                  "Lower latency via QUIC",
                  "Multiplexed streams, no head-of-line blocking",
                ].map((feat) => (
                  <li key={feat} className="text-muted-foreground flex items-center gap-3 text-sm">
                    <CheckCircle2
                      size={14}
                      className="text-brand-green shrink-0"
                      aria-hidden="true"
                    />
                    {feat}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div {...fadeUp(0.12)}>
              <ProtocolDiagram />
            </motion.div>
          </div>
        </div>
      </section> */}
    </>
  );
}
